import type { VentaRow } from '@/lib/ventas/types';

interface Props {
  venta: VentaRow | null;
  loading: boolean;
  onClose: () => void;
}

function formatBs(val: number): string {
  return `Bs. ${Number(val || 0).toFixed(2)}`;
}

export default function DetalleVentaModal({ venta, loading, onClose }: Props) {
  if (!venta) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 animate-fade-in" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto animate-scale-in">
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-6 py-4 border-b border-background-200 rounded-t-2xl">
          <h2 className="font-heading text-lg font-semibold text-foreground-800">Detalle de Venta</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-foreground-400 mb-0.5">Fecha</p>
              <p className="font-medium text-foreground-800">{new Date(venta.fecha).toLocaleString('es-ES')}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-400 mb-0.5">Método de Pago</p>
              <p className="font-medium text-foreground-800">{venta.metodo_pago}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-400 mb-0.5">Cliente</p>
              <p className="font-medium text-foreground-800">{venta.nombre_nit || 'Consumidor Final'}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-400 mb-0.5">NIT / CI</p>
              <p className="font-medium text-foreground-800">{venta.numero_nit || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-400 mb-0.5">Atendió</p>
              <p className="font-medium text-foreground-800">{venta.usuario_nombre}</p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
            </div>
          ) : venta.detalle && venta.detalle.length > 0 ? (
            <div>
              <h4 className="text-sm font-semibold text-foreground-700 mb-2">Productos</h4>
              <div className="border border-background-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-background-50 border-b border-background-200">
                      <th className="text-left px-3 py-2 text-xs font-semibold text-foreground-500">Producto</th>
                      <th className="text-center px-3 py-2 text-xs font-semibold text-foreground-500">Cant</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-foreground-500">P. Unit</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-foreground-500">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {venta.detalle.map((d) => (
                      <tr key={d.id} className="border-b border-background-100 last:border-0">
                        <td className="px-3 py-2 text-sm text-foreground-700">{d.producto_nombre}</td>
                        <td className="px-3 py-2 text-sm text-center text-foreground-600">{d.cantidad}</td>
                        <td className="px-3 py-2 text-sm text-right text-foreground-600">{formatBs(Number(d.precio_unitario))}</td>
                        <td className="px-3 py-2 text-sm text-right font-medium text-foreground-700">{formatBs(Number(d.subtotal))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-sm text-foreground-400 text-center py-4">Sin detalle de productos</p>
          )}

          <div className="flex justify-between items-center pt-3 border-t border-background-200">
            <div>
              {venta.metodo_pago === 'Efectivo' && Number(venta.monto_recibido) > 0 && (
                <div className="text-xs text-foreground-500">
                  <p>Recibido: {formatBs(Number(venta.monto_recibido))}</p>
                  <p>Cambio: {formatBs(Number(venta.cambio))}</p>
                </div>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs text-foreground-400">Total</p>
              <p className="text-xl font-bold text-primary-600">{formatBs(Number(venta.total))}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}